<script setup lang="ts">
import { ProjectMapTemplate } from '@beeboard/common';
import AppProjectCopy from '@/app/project/AppProjectCopy.vue';
import EditProject from '@/app/project/EditProject.vue';
import StartOrEditMeeting from '@/app/project/meetings/StartOrEditMeeting.vue';
import UpdateProjectFromTemplate from '@/app/project/updateFromTemplate/UpdateProjectFromTemplate.vue';
import { renderMapToSvg } from '@/app/remap/canvas/export/renderMapSvg';
import ExcelExportModal from '@/app/remap/components/ExcelExportModal.vue';
import MapPlannerModal from '@/app/remap/components/MapPlannerModal.vue';
import AppTemplateCopy from '@/app/template/AppTemplateCopy.vue';
import AppTemplateForm from '@/app/template/AppTemplateForm.vue';
import { useAiLiveStore } from '@/store/aiLiveStore';
import { useIoStore } from '@/store/ioStore';
import { useMeetingStore } from '@/store/meetingStore';
import { useNotificationStore } from '@/store/notificationStore';
import { usePresenceStore } from '@/store/presenceStore';
import { useProfileStore } from '@/store/profileStore';
import { useProjectRulesStore } from '@/store/projectRulesStore';
import { useProjectStore } from '@/store/projectStore';
import { useMapStore } from '@/store/remapStore';
import { useSocketStore } from '@/store/socketStore';
import { useTemplateRulesStore } from '@/store/templateRulesStore';
import { useTemplateStore } from '@/store/templateStore';
import { useUiStore } from '@/store/uiStore';
import { useUserRulesStore } from '@/store/userRulesStore';
import { DropdownOption } from '@/ui/AppDropdown.vue';
import { exportTemplateExcelFile } from '@/utils/helpers/excel-helper';
import { getMSIntegrationConfig } from '@/utils/helpers/msIntegrationHelpers';
import { generateMapPdfFromSvg } from '@/utils/helpers/pdf-helper';
import HeaderProjectTree from './MapHeaderProjectTree.vue';
import MapPresenceAvatars from './MapPresenceAvatars.vue';

const mapStore = useMapStore();
const presenceStore = usePresenceStore();
const aiLiveStore = useAiLiveStore();

const aiMode = computed(() => (mapStore.isTemplate ? 'template' : 'project'));

// The map follows the AI assistant — once it saved something to follow
// (WI #5767).
const isFollowingAi = computed(
  () =>
    aiLiveStore.isFollowing(aiMode.value, mapStore.mapId) &&
    !!aiLiveStore.ringFor(aiMode.value, mapStore.mapId),
);

function unfollowAi() {
  aiLiveStore.stopFollowing(aiMode.value, mapStore.mapId);
}

const followedUserName = computed(() => {
  const user = presenceStore.followedUser;
  if (!user) {
    return '';
  }

  return [user.first_name, user.last_name].filter(Boolean).join(' ') || '?';
});

function kickAllFollowers() {
  const socket = useSocketStore().socketInstance;
  if (socket) {
    socket.emit('follow:kick', {
      projectId: route.params.projectId as string,
    });
  }
  presenceStore.followers = [];
  presenceStore.localMarkupStrokes = [];
  presenceStore.currentDrawingPoints = [];
}

const followerNames = computed(() => {
  return presenceStore.followers
    .map((id) => {
      const user = presenceStore.activeUsers[id];
      if (!user) {
        return '?';
      }

      return [user.first_name, user.last_name].filter(Boolean).join(' ') || '?';
    })
    .join(', ');
});
const uiStore = useUiStore();
const templateStore = useTemplateStore();
const projectStore = useProjectStore();
const profileStore = useProfileStore();
const meetingStore = useMeetingStore();
const ioStore = useIoStore();
const notificationStore = useNotificationStore();
const templateRulesStore = useTemplateRulesStore();
const projectRulesStore = useProjectRulesStore();
const userRulesStore = useUserRulesStore();
const route = useRoute();
const router = useRouter();
const message = useAlerts();

const startMeetingDialogIsVisible = ref(false);
const meetingMode = ref<'start' | 'edit'>('start');
const activeMeetingId = ref('');
const copyTemplateDialogIsVisible = ref(false);
const copyProjectDialogIsVisible = ref(false);
const editTemplateDialogIsVisible = ref(false);
const editProjectDialogIsVisible = ref(false);
const exportExcelModalIsVisible = ref(false);
const exportToPlannerIsVisible = ref(false);
const updateFromTemplateIsVisible = ref(false);
const selectedTemplate = ref<ProjectMapTemplate | null>(null);
const isReloadProjectInProgress = ref(false);

const projectId = route.params.projectId as string;

const isResponsible = computed(() => {
  if (mapStore.isTemplate) {
    return templateRulesStore.isUserTemplateOwner(mapStore.mapId);
  }

  return projectRulesStore.isProjectResponsible(mapStore.mapId);
});

const isUserEditorOrAbove = computed(() => {
  if (mapStore.isTemplate) {
    return templateRulesStore.isUserTemplateEditorOrAbove(mapStore.mapId);
  }

  return projectRulesStore.isProjectEditorOrAbove(mapStore.mapId);
});

const isUserProjectResponsible = computed(() => {
  if (mapStore.isTemplate) {
    return false;
  }

  return projectRulesStore.isProjectResponsible(mapStore.mapId);
});

const canUserManageProjectResponsible = computed(() => {
  return userRulesStore.canUserManageProjectResponsible();
});

const canUserCopyProject = computed(() => {
  return (
    isUserProjectResponsible.value || canUserManageProjectResponsible.value
  );
});

const canUserCopyTemplate = computed(
  () =>
    mapStore.isTemplate &&
    templateRulesStore.canUserCopyTemplate(mapStore.mapId),
);

const msIntegrationConfig = getMSIntegrationConfig();

const dropDownOptionsProject = computed((): DropdownOption[] => [
  {
    label: $t('template.renumberActivities'),
    value: 'renumberActivities',
    icon: 'RefreshCw',
    disabled: !isUserEditorOrAbove.value,
    id: 'renumberActivitiesProject',
  },
  {
    label: $t('template.renumberMilestones'),
    value: 'renumberMilestones',
    icon: 'RefreshCw',
    disabled: !isUserEditorOrAbove.value,
    id: 'renumberMilestonesProject',
  },
  ...(meetingStore.activeMeeting
    ? [
        {
          label: $t('projectMeetings.endMeeting'),
          value: 'endMeeting',
          icon: 'Octagon' as const,
          id: 'endMeetingProject',
        },
        {
          label: $t('projectMeetings.detailMeeting'),
          value: 'meetingDetails',
          icon: 'AlignJustify' as const,
          id: 'meetingDetailsProject',
        },
      ]
    : [
        {
          label: $t('projectMeetings.startMeeting'),
          value: 'startMeeting',
          icon: 'Play' as const,
          id: 'startMeetingProject',
        },
      ]),
  {
    label: $t('project.exportExcel'),
    value: 'exportExcel',
    icon: 'ArrowDown',
    disabled: !userRulesStore.canUserExport(),
    id: 'exportExcelProject',
  },
  {
    label: $t('map.exportPDF'),
    value: 'exportPDF',
    icon: 'ArrowDown',
    disabled: !userRulesStore.canUserExport(),
    id: 'exportPDFProject',
  },
  ...(msIntegrationConfig.hasAnyEnabled
    ? ([
        {
          label: $t('map.exportToPlanner'),
          value: 'exportToPlanner',
          icon: 'ArrowDown',
          id: 'exportPlannerProject',
        },
      ] as DropdownOption[])
    : ([] as DropdownOption[])),
  {
    label: $t('project.updateFromTemplate'),
    value: 'updateFromTemplate',
    icon: 'ArrowsUpFromLine',
    disabled: !isUserEditorOrAbove.value,
    id: 'updateFromTemplate',
  },
  {
    label: $t('project.copyProject'),
    value: 'copyProject',
    icon: 'Copy',
    disabled: !canUserCopyProject.value,
    id: 'copyProject',
  },
  {
    label: $t('volatilityAnalytics.title'),
    value: 'volatilityAnalytics',
    icon: 'Gauge',
    id: 'volatilityAnalyticsProject',
  },
  {
    label: $t('checklist.poolEditorTitle'),
    value: 'checklistRows',
    icon: 'CheckSquare',
    id: 'checklistRowsProject',
  },
]);

const dropDownOptionsTemplate = computed((): DropdownOption[] => [
  {
    label: $t('template.renumberActivities'),
    value: 'renumberActivities',
    icon: 'RefreshCw',
    disabled: !isUserEditorOrAbove.value,
    id: 'renumberActivitiesTemplate',
  },
  {
    label: $t('template.renumberMilestones'),
    value: 'renumberMilestones',
    icon: 'RefreshCw',
    disabled: !isUserEditorOrAbove.value,
    id: 'renumberMilestonesTemplate',
  },
  {
    label: $t('template.copyTemplate'),
    value: 'copyTemplate',
    icon: 'Copy',
    disabled: !canUserCopyTemplate.value,
    id: 'copyTemplate',
  },
  {
    label: $t('project.exportExcel'),
    value: 'exportTemplateExcel',
    icon: 'ArrowDown',
    disabled: !userRulesStore.canUserExport(),
    id: 'exportExcelTemplate',
  },
  {
    label: $t('map.exportPDF'),
    value: 'exportPDF',
    icon: 'ArrowDown',
    disabled: !userRulesStore.canUserExport(),
    id: 'exportPDFTemplate',
  },
  // Per-template quality-check defaults (WI #5431). Reached from here rather
  // than the side navigation: it is a rarely-touched per-template setting, and
  // the map is where the checks are actually seen.
  {
    label: $t('templateQuality.settings.navLabel'),
    value: 'qualityCheckSettings',
    icon: 'CheckCircle',
    disabled: !isUserEditorOrAbove.value,
    id: 'qualityCheckSettingsTemplate',
  },
]);

const templateStatus = computed(() =>
  mapStore.isTemplate
    ? templateStore.templateStatus(mapStore.mapId)
    : undefined,
);

async function handleDropdownSelect(option: string) {
  if (option === 'renumberActivities') {
    await renumberActivities();
  } else if (option === 'renumberMilestones') {
    await renumberMilestones();
  } else if (option === 'copyTemplate') {
    copyTemplate();
  } else if (option === 'updateFromTemplate') {
    updateFromTemplate();
  } else if (option === 'copyProject') {
    copyProject();
  } else if (option === 'exportExcel') {
    exportExcel();
  } else if (option === 'exportTemplateExcel') {
    await exportExcelTemplate();
  } else if (option === 'exportPDF') {
    await exportPdf();
  } else if (option === 'exportToPlanner') {
    await exportToPlanner();
  } else if (option === 'startMeeting') {
    await startMeeting();
  } else if (option === 'endMeeting') {
    await endMeeting();
  } else if (option === 'meetingDetails') {
    await meetingDetails();
  } else if (option === 'volatilityAnalytics') {
    await router.push({
      name: 'ProjectVolatilityAnalytics',
      params: { projectId },
    });
  } else if (option === 'checklistRows') {
    await router.push({
      name: 'ProjectSettingsChecklistRows',
      params: { projectId },
    });
  } else if (option === 'qualityCheckSettings') {
    await router.push({
      name: 'TemplateQualityChecks',
      params: { templateId: mapStore.mapId },
    });
  }
}

const title = computed(() =>
  mapStore.isTemplate
    ? templateStore.templateTitle(route.params.templateId as string)
    : projectStore.projectTitle(route.params.projectId as string),
);

const projectIsNotActive = computed(
  () =>
    !mapStore.isTemplate &&
    projectStore.hasProjectSummary(route.params.projectId as string) &&
    !projectStore.projectSummary(route.params.projectId as string)?.is_active,
);

// Mirrors the project's "Project is not active" line: the map says plainly when
// the thing you are editing is not the normal, usable state.
const templateStatusNote = computed(() => {
  if (templateStatus.value === 'draft') {
    return $t('template.isDraftState');
  }
  if (templateStatus.value === 'inactive') {
    return $t('template.isNotActive');
  }

  return '';
});

function showEditDialog() {
  mapStore.toolMode = 'normal';
  mapStore.selectedContext = '';
  mapStore.selectedId = '';

  return nextTick(() => {
    if (!mapStore.isTemplate) {
      editProjectDialogIsVisible.value = true;

      return;
    }
    selectedTemplate.value = templateStore.template(mapStore.mapId);
    editTemplateDialogIsVisible.value = true;
  });
}

function copyTemplate() {
  copyTemplateDialogIsVisible.value = true;
  selectedTemplate.value = templateStore.template(mapStore.mapId);
}

function exportToPlanner() {
  exportToPlannerIsVisible.value = true;
}

function updateFromTemplate() {
  updateFromTemplateIsVisible.value = true;
}

function copyProject() {
  copyProjectDialogIsVisible.value = true;
}

function exportExcel() {
  exportExcelModalIsVisible.value = true;
}

function exportExcelTemplate() {
  const template = templateStore.template(mapStore.mapId);

  return exportTemplateExcelFile(template, $t);
}

function startMeeting() {
  meetingMode.value = 'start';
  startMeetingDialogIsVisible.value = true;

  return clearUrl();
}

function meetingDetails() {
  if (!meetingStore.activeMeeting) {
    return;
  }
  meetingMode.value = 'edit';
  startMeetingDialogIsVisible.value = true;
  activeMeetingId.value = meetingStore.activeMeeting.id;
}

function endMeeting() {
  if (!meetingStore.activeMeeting) {
    return;
  }

  return meetingStore
    .endMeeting(mapStore.mapId, meetingStore.activeMeeting.id)
    .then(() => {
      activeMeetingId.value = '';
      message.success($t('projectMeetings.meetingEnded'));
    })
    .catch(message.error);
}

function onCloseMeetingDialog() {
  startMeetingDialogIsVisible.value = false;

  return clearUrl();
}

function renumberActivities() {
  const store = mapStore.isTemplate ? templateStore : projectStore;

  return store
    .update(mapStore.mapId, {
      _actions: {
        renumber_activities: true,
        renumber_milestones: false,
      },
    })
    .then(() => message.success($t('template.activitiesRenumbered')))
    .catch(message.error);
}

function renumberMilestones() {
  const store = mapStore.isTemplate ? templateStore : projectStore;

  return store
    .update(mapStore.mapId, {
      _actions: { renumber_activities: false, renumber_milestones: true },
    })
    .then(() => message.success($t('template.milestonesRenumbered')))
    .catch(message.error);
}

async function exportPdf() {
  mapStore.isExportingPDF = true;
  mapStore.setOffset(0);
  await nextTick();

  try {
    // The scene SVG is gated off the live DOM, so render the whole map to a
    // vector SVG with the same painters and print that markup.
    const { svg, width, height } = renderMapToSvg();
    await generateMapPdfFromSvg(svg, height, width, title.value);
    message.success();
  } catch {
    message.error($t('errors.popupsMustBeEnabled'));
  } finally {
    mapStore.isExportingPDF = false;
    mapStore.animateMap(mapStore.zoom, ioStore.firstVisiblePhase.start);
  }
}

function clearUrl() {
  return router.replace({ name: 'ProjectPage', query: {} });
}

async function reloadProject() {
  try {
    isReloadProjectInProgress.value = true;
    await projectStore.loadProject(projectId);
  } finally {
    isReloadProjectInProgress.value = false;
    notificationStore.isProjectRefreshRequired = false;
  }
}

onMounted(() => {
  if (route.query.openMeetingDialog) {
    startMeetingDialogIsVisible.value = true;
  }
});
</script>

<template>
  <header class="header">
    <UiOverlay v-if="mapStore.isTemplate" v-model="editTemplateDialogIsVisible">
      <AppTemplateForm
        v-if="selectedTemplate"
        :template-id="selectedTemplate.id"
        @close="editTemplateDialogIsVisible = false"
      />
    </UiOverlay>

    <UiOverlay v-if="!mapStore.isTemplate" v-model="editProjectDialogIsVisible">
      <EditProject
        :project-id="mapStore.mapId"
        @close="editProjectDialogIsVisible = false"
      />
    </UiOverlay>

    <UiOverlay v-model="copyTemplateDialogIsVisible">
      <AppTemplateCopy
        v-if="selectedTemplate"
        :template-id="selectedTemplate.id"
        @copied="copyTemplateDialogIsVisible = false"
        @close="copyTemplateDialogIsVisible = false"
      />
    </UiOverlay>

    <UiOverlay v-if="!mapStore.isTemplate" v-model="copyProjectDialogIsVisible">
      <AppProjectCopy
        :project-id="mapStore.mapId"
        @close="copyProjectDialogIsVisible = false"
      />
    </UiOverlay>

    <UiOverlay v-model="exportExcelModalIsVisible">
      <ExcelExportModal @close="exportExcelModalIsVisible = false" />
    </UiOverlay>

    <UiOverlay v-model="exportToPlannerIsVisible">
      <MapPlannerModal @close="exportToPlannerIsVisible = false" />
    </UiOverlay>

    <UiOverlay v-model="updateFromTemplateIsVisible">
      <UpdateProjectFromTemplate @close="updateFromTemplateIsVisible = false" />
    </UiOverlay>

    <UiOverlay v-model="startMeetingDialogIsVisible">
      <StartOrEditMeeting
        :project-id="mapStore.mapId"
        :meeting-id="activeMeetingId"
        :mode="meetingMode"
        @close="onCloseMeetingDialog"
      />
    </UiOverlay>

    <div class="spacer" />

    <HeaderProjectTree v-if="!mapStore.isTemplate" />

    <div class="info">
      <div class="title">
        {{ title }}
      </div>

      <UiButton
        v-if="isResponsible"
        variant="primary-text"
        @click="showEditDialog"
      >
        <UiIcon name="Edit3" class="icon" />
      </UiButton>
    </div>

    <div class="spacer" />

    <!-- Colleagues on project maps, the AI assistant on both (WI #5767) -->
    <MapPresenceAvatars />

    <div v-if="presenceStore.isFollowing" class="following-banner">
      <UiIcon name="Eye" class="following-icon" />
      <span>{{
        $t('presence.followingBanner', { name: followedUserName })
      }}</span>
      <button class="following-stop" @click="presenceStore.unfollowUser()">
        <UiIcon name="XCircle" class="following-stop-icon" />
      </button>
    </div>
    <div
      v-else-if="isFollowingAi"
      class="following-banner"
      data-testid="ai-following-banner"
    >
      <UiIcon name="Eye" class="following-icon" />
      <span>{{
        $t('presence.followingBanner', { name: $t('presence.aiAssistant') })
      }}</span>
      <button class="following-stop" @click="unfollowAi">
        <UiIcon name="XCircle" class="following-stop-icon" />
      </button>
    </div>

    <div
      v-if="presenceStore.isBeingFollowed"
      class="following-banner being-followed-banner"
    >
      <UiIcon name="Eye" class="following-icon" />
      <span>{{
        $t('presence.beingFollowedBanner', { names: followerNames })
      }}</span>
      <button class="following-stop" @click="kickAllFollowers">
        <UiIcon name="XCircle" class="following-stop-icon" />
      </button>
    </div>

    <div
      v-if="!mapStore.isTemplate && notificationStore.isProjectRefreshRequired"
      class="refresh-project"
    >
      <UiPopover theme="primary" placement="bottom">
        <template #default>
          <UiButton
            class="refresh-button"
            variant="flat"
            @click="reloadProject"
          >
            <UiIcon name="RefreshCw" class="refresh-icon" />
            <span class="refresh-badge" />
          </UiButton>
        </template>

        <template #content>
          {{ $t('project.projectOutdated') }}
        </template>
      </UiPopover>
    </div>

    <div
      v-if="meetingStore.activeMeeting && !mapStore.isTemplate"
      class="meeting-is-active"
    >
      <span class="meeting-icon"></span>
      <span>{{ $t('projectMeetings.meetingInProgress') }}</span>
    </div>

    <div v-if="projectIsNotActive" class="project-is-not-active">
      <span>{{ $t('project.isNotActive') }}</span>
    </div>

    <div
      v-if="templateStatusNote"
      class="template-status-note"
      :class="`template-status-note--${templateStatus}`"
    >
      <span>{{ templateStatusNote }}</span>
    </div>

    <UiSpinner
      v-if="profileStore.isLoading || isReloadProjectInProgress"
      :spin="true"
    />

    <div class="actions">
      <AppDropdown
        v-if="!uiStore.pendingOperation"
        :options="
          mapStore.isTemplate ? dropDownOptionsTemplate : dropDownOptionsProject
        "
        @input="(option: string) => handleDropdownSelect(option)"
      />
    </div>
  </header>
</template>

<style lang="scss" scoped>
.header {
  display: flex;
  justify-content: stretch;
  align-items: center;
  padding: 0 var(--space-8);
  height: px(52);
  width: 100%;
}

.icon {
  width: px(16);
  height: px(16);
}

.info {
  display: flex;
  align-items: center;
  gap: var(--space-8);

  .title {
    font-size: var(--font-size-18);
    font-weight: var(--font-weight-bold);
    display: flex;
    justify-content: space-between;
    width: 103%;
  }
}

.spacer {
  flex: 1 1 auto;
}

.following-banner {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  padding: var(--space-4) var(--space-10);
  margin-right: var(--space-8);
  border-radius: var(--border-radius-6);
  background: var(--color-primary-50);
  border: 1px solid var(--color-primary-200);
  color: var(--color-primary-700);
  font-size: var(--font-size-12);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.following-icon {
  width: px(14);
  height: px(14);
  color: var(--color-primary-500);
}

.following-stop {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 0;
  margin-left: var(--space-2);
  cursor: pointer;
  color: var(--color-primary-400);
  transition: color var(--duration-base);

  &:hover {
    color: var(--color-primary-700);
  }
}

.following-stop-icon {
  width: px(14);
  height: px(14);
}

.refresh-project {
}

.being-followed-banner {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
  color: #065f46;

  .following-icon {
    color: #10b981;
  }
}

.refresh-button {
  margin-right: var(--space-8);
  position: relative;
  animation: bounce 0.5s ease-in-out infinite;
  animation-delay: 0s;
  animation-fill-mode: both;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-direction: normal;
  animation-play-state: running;
  animation-duration: 2s;
}

.refresh-badge {
  position: absolute;
  top: 10px;
  left: 35px;
  background-color: red;
  border-radius: var(--border-radius-circle);
  width: 8px;
  height: 8px;
}

.actions {
  display: flex;
  align-items: center;
  gap: var(--space-8);
}

.ui-spinner {
  margin-right: var(--space-8);
  margin-left: var(--space-8);
}
.button-class {
  border: 0;
}

.meeting-is-active,
.project-is-not-active {
  color: var(--color-error-500);
  display: flex;
  flex-shrink: 0;
  align-items: center;
  font-weight: var(--font-weight-medium);
}

.template-status-note {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  font-weight: var(--font-weight-medium);

  // A draft is work in progress, not a fault -- only inactive gets the red the
  // project banner uses.
  &--draft {
    color: var(--color-warning-500);
  }

  &--inactive {
    color: var(--color-error-500);
  }
}

.meeting-icon {
  width: px(16);
  height: px(16);
  background-color: currentColor;
  border-radius: var(--border-radius-circle);
  display: inline-block;
  margin-right: var(--space-8);
}

@keyframes blink {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes bounce {
  0%,
  33.33%,
  100% {
    transform: translateY(0);
  } /* 33.33% = 1s of 3s total */
  16.66% {
    transform: translateY(-10px);
  } /* Halfway bounce */
}
</style>
